/**
 * The difficulty and wave-speed pickers.
 *
 * Moved here out of the start gate rather than rewritten, because there must be
 * exactly one of each in the codebase. A second difficulty selector — even one
 * that looks identical — is a second place for the rule "you cannot change this
 * mid-song" to be forgotten, and a second answer to "what counts as a
 * difficulty".
 *
 * Both are dumb: they draw what they are given and report clicks. Which
 * difficulties exist for a song, whether a switch is allowed, and what a speed
 * means are all decided above them.
 */

const STYLE_ID = 'mandalizm-picker-style';

const CSS = `
.mandalizm-picker__label {
  font-size: 10px; letter-spacing: 0.22em; color: rgba(140, 205, 220, 0.6);
  margin-bottom: 8px;
}
.mandalizm-picker__difficulties { display: flex; gap: 8px; margin-bottom: 18px; }
.mandalizm-picker__difficulty {
  appearance: none; cursor: pointer; font: inherit; flex: 1 1 0; min-width: 88px;
  padding: 9px 6px; color: #b6e8e4; text-align: center;
  background: rgba(3, 30, 38, 0.72); border: 1px solid rgba(90, 240, 220, 0.22);
  border-radius: 6px; transition: background 140ms ease, border-color 140ms ease, color 140ms ease;
}
.mandalizm-picker__difficulty:hover:not(:disabled) { background: rgba(6, 62, 70, 0.9); }
.mandalizm-picker__difficulty[aria-pressed="true"] {
  color: #eafeff; background: rgba(8, 96, 104, 0.95);
  border-color: rgba(120, 250, 232, 0.75);
  box-shadow: 0 0 14px rgba(0, 220, 200, 0.22) inset;
}
.mandalizm-picker__difficulty:disabled { opacity: 0.4; cursor: not-allowed; }
.mandalizm-picker__difficulty b { display: block; font-weight: 600; letter-spacing: 0.16em; font-size: 12px; }
.mandalizm-picker__difficulty small {
  display: block; margin-top: 3px; font-size: 10px;
  color: rgba(150, 215, 225, 0.68); letter-spacing: 0.06em;
}
.mandalizm-picker__speeds { display: flex; gap: 6px; margin-bottom: 18px; flex-wrap: wrap; }
.mandalizm-picker__speed {
  appearance: none; cursor: pointer; font: inherit; font-size: 11px; letter-spacing: 0.08em;
  padding: 6px 11px; color: #9fd8dd;
  background: rgba(3, 26, 34, 0.7); border: 1px solid rgba(90, 240, 220, 0.18);
  border-radius: 5px; transition: background 140ms ease, border-color 140ms ease, color 140ms ease;
}
.mandalizm-picker__speed:hover:not(:disabled) { background: rgba(6, 58, 66, 0.9); }
.mandalizm-picker__speed[aria-pressed="true"] {
  color: #eafeff; background: rgba(8, 92, 100, 0.95); border-color: rgba(120, 250, 232, 0.7);
}
.mandalizm-picker__speed:disabled { opacity: 0.4; cursor: not-allowed; }
`;

/** One selectable chart, as the picker needs to show it. */
export interface DifficultyChoice {
  readonly value: string;
  readonly label: string;
  /** Events in that chart. Shown so the choice is legible before committing. */
  readonly eventCount: number;
}

export class DifficultyRow {
  readonly element: HTMLDivElement;
  private readonly label: HTMLDivElement;
  private readonly row: HTMLDivElement;
  private readonly buttons = new Map<string, HTMLButtonElement>();
  private onSelect: ((value: string) => void | Promise<void>) | null = null;
  private busy = false;

  constructor(labelText = 'DIFFICULTY') {
    injectStyle();
    this.element = document.createElement('div');
    this.label = document.createElement('div');
    this.label.className = 'mandalizm-picker__label';
    this.label.textContent = labelText;
    this.row = document.createElement('div');
    this.row.className = 'mandalizm-picker__difficulties';
    this.row.setAttribute('role', 'group');
    this.row.setAttribute('aria-label', 'Difficulty');
    this.element.append(this.label, this.row);
    this.setVisible(false);
  }

  /**
   * Offers a song's charts.
   *
   * Called with the counts, because showing "NORMAL" without saying whether that
   * is 40 events or 500 is the part that makes a difficulty picker useless.
   * Only difficulties actually present in the song are passed in.
   */
  setChoices(
    choices: readonly DifficultyChoice[],
    current: string,
    onSelect: (value: string) => void | Promise<void>,
  ): void {
    this.onSelect = onSelect;
    this.buttons.clear();
    this.row.replaceChildren();

    for (const choice of choices) {
      const button = document.createElement('button');
      button.className = 'mandalizm-picker__difficulty';
      button.type = 'button';
      button.setAttribute('aria-pressed', String(choice.value === current));

      const name = document.createElement('b');
      name.textContent = choice.label;
      const count = document.createElement('small');
      count.textContent = `${choice.eventCount.toLocaleString()} events`;
      button.append(name, count);

      button.addEventListener('click', () => {
        if (this.busy || button.getAttribute('aria-pressed') === 'true') return;
        void this.pick(choice.value);
      });

      this.buttons.set(choice.value, button);
      this.row.appendChild(button);
    }
    this.setVisible(choices.length > 0);
  }

  /** Moves the pressed state, after the caller has actually switched. */
  setValue(value: string): void {
    for (const [key, button] of this.buttons) {
      button.setAttribute('aria-pressed', String(key === value));
    }
  }

  setDisabled(disabled: boolean): void {
    this.busy = disabled;
    for (const button of this.buttons.values()) button.disabled = disabled;
  }

  setVisible(visible: boolean): void {
    this.element.hidden = !visible;
  }

  private async pick(value: string): Promise<void> {
    this.setDisabled(true);
    try {
      await this.onSelect?.(value);
    } finally {
      this.setDisabled(false);
    }
  }
}

export class WaveSpeedRow {
  readonly element: HTMLDivElement;
  private readonly row: HTMLDivElement;
  private readonly buttons = new Map<string, HTMLButtonElement>();
  private onSelect: ((value: number) => void | Promise<void>) | null = null;
  private busy = false;

  constructor(labelText = 'WAVE SPEED') {
    injectStyle();
    this.element = document.createElement('div');
    const label = document.createElement('div');
    label.className = 'mandalizm-picker__label';
    label.textContent = labelText;
    this.row = document.createElement('div');
    this.row.className = 'mandalizm-picker__speeds';
    this.row.setAttribute('role', 'group');
    this.row.setAttribute('aria-label', 'Wave speed');
    this.element.append(label, this.row);
  }

  /**
   * A separate row from the difficulty, and labelled separately, because they
   * are different questions. Difficulty asks how many waves; this asks how long
   * you get to look at each one.
   */
  setPresets(
    presets: readonly number[],
    current: number,
    onSelect: (value: number) => void | Promise<void>,
  ): void {
    this.onSelect = onSelect;
    this.buttons.clear();
    this.row.replaceChildren();
    for (const preset of presets) {
      const button = document.createElement('button');
      button.className = 'mandalizm-picker__speed';
      button.type = 'button';
      button.textContent = formatSpeed(preset);
      button.setAttribute('aria-pressed', String(Math.abs(preset - current) < 1e-6));
      button.addEventListener('click', () => {
        if (this.busy || button.getAttribute('aria-pressed') === 'true') return;
        void this.pick(preset);
      });
      this.buttons.set(formatSpeed(preset), button);
      this.row.appendChild(button);
    }
  }

  setValue(value: number): void {
    const key = formatSpeed(value);
    for (const [name, button] of this.buttons) {
      button.setAttribute('aria-pressed', String(name === key));
    }
  }

  setDisabled(disabled: boolean): void {
    this.busy = disabled;
    for (const button of this.buttons.values()) button.disabled = disabled;
  }

  private async pick(value: number): Promise<void> {
    this.setDisabled(true);
    try {
      await this.onSelect?.(value);
    } finally {
      this.setDisabled(false);
    }
  }
}

/** "1x", "0.5x", "1.25x" — trailing zeros dropped so the row stays scannable. */
export function formatSpeed(value: number): string {
  return `${Number(value.toFixed(2))}x`;
}

function injectStyle(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}
